import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Clock, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { differenceInMinutes } from 'date-fns';
import { logRealtimeStatus } from '@/utils/realtimeStatusLog';
// DTC-J: route the time-clock lifecycle write timestamps + total
// minutes calculation through the canonical clock so persisted
// `clock_in_time` / `clock_out_time` writes and `total_minutes`
// derivations are deterministic under `clock.setFixed(...)`. The L144
// elapsed-display ticker remains class-A display-only.
import { clock } from '@/utils/clock';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
export const TimeClockButton = ({ refreshKey }: { refreshKey?: number }) => {
  const { activeShop } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [activeEntry, setActiveEntry] = useState<any>(null);
  const [elapsedTime, setElapsedTime] = useState('');
  const [timeClockEnabled, setTimeClockEnabled] = useState(false);
  const [technicianId, setTechnicianId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [showReasonDialog, setShowReasonDialog] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string>('Lunch');
  const [customReason, setCustomReason] = useState('');
  useEffect(() => {
    if (!activeShop?.id) return;

    // Check if time clock is enabled
    const checkSettings = async () => {
      const { data } = await supabase
        .from('shop_profile')
        .select('time_clock_enabled')
        .eq('shop_id', activeShop.id)
        .maybeSingle();

      setTimeClockEnabled(data?.time_clock_enabled ?? false);
    };

    checkSettings();

    // Phase 2.1: Real-time subscription for toggle changes.
    //
    // Chronomancer R-RT-3: cleanup MUST go through `supabase.removeChannel`
    // in Supabase JS v2. `channel.unsubscribe()` stops receiving payloads
    // but leaves the channel registered on the client, consuming a slot
    // against the socket's per-connection channel quota. Over long-lived
    // sessions (advisor leaves TopBar mounted all day) that drift would
    // exhaust the quota and silently drop new subscriptions. Matches the
    // cleanup pattern used by Birdseye.tsx:313-315 and every other
    // realtime site in this audit.
    const channel = supabase
      .channel(`timeclock-profile-${activeShop.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'shop_profile',
          filter: `shop_id=eq.${activeShop.id}`,
        },
        (payload) => {
          if ('time_clock_enabled' in payload.new) {
            setTimeClockEnabled(payload.new.time_clock_enabled ?? false);
          }
        }
      )
      .subscribe((status, err) => {
        // Batch 11: normalized log. Intentional CLOSED on shop change
        // / unmount → debug. Real failures (CHANNEL_ERROR, TIMED_OUT,
        // CLOSED-with-err) still warn/error so a stale time-clock
        // toggle is diagnosable.
        logRealtimeStatus('[TimeClockButton]', status, err);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeShop?.id]);

  useEffect(() => {
    if (!activeShop?.id || !timeClockEnabled) return;

    // Get user ID and technician ID for current user
    const getUserData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setUserId(user.id);

      const { data } = await supabase
        .from('technician_users')
        .select('technician_id')
        .eq('user_id', user.id)
        .eq('shop_id', activeShop.id)
        .maybeSingle();

      if (data?.technician_id) {
        setTechnicianId(data.technician_id);
      }
    };

    getUserData();
  }, [activeShop?.id, timeClockEnabled]);

  useEffect(() => {
    if (!userId || !timeClockEnabled) return;

    // Load active clock-in entry
    const loadActiveEntry = async () => {
      let query = supabase
        .from('time_clock_entries')
        .select('*')
        .is('clock_out_time', null);
      
      if (technicianId) {
        query = query.eq('technician_id', technicianId);
      } else {
        query = query.eq('user_id', userId);
      }
      
      const { data } = await query.maybeSingle();
      setActiveEntry(data);
    };

    loadActiveEntry();
    // Re-fetch when Dashboard's shared timeclock listener fires
  }, [userId, technicianId, timeClockEnabled, refreshKey]);

  // Update elapsed time display
  useEffect(() => {
    if (!activeEntry) {
      setElapsedTime('');
      return;
    }

    const updateElapsed = () => {
      const minutes = differenceInMinutes(new Date(), new Date(activeEntry.clock_in_time));
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      setElapsedTime(`${hours}h ${mins}m`);
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [activeEntry]);

  const handleClockIn = async () => {
    if (!userId || !activeShop?.id) return;

    setIsLoading(true);
    try {
      // Phase 2.2: Re-check toggle before proceeding
      const { data: settings } = await supabase
        .from('shop_profile')
        .select('time_clock_enabled')
        .eq('shop_id', activeShop.id)
        .maybeSingle();

      if (!settings?.time_clock_enabled) {
        toast.error('The time clock feature has been turned off. Please contact your manager.');
        setIsLoading(false);
        return;
      }

      // Record current time in UTC (browser will send correct UTC timestamp)
      const now = clock.now();

      const { error } = await supabase
        .from('time_clock_entries')
        .insert({
          user_id: userId,
          technician_id: technicianId || null,
          shop_id: activeShop.id,
          clock_in_time: now.toISOString(),
          is_after_hours: false, // No longer checking after hours
          auto_clocked_in: false
        });

      if (error) throw error;

      toast.success('Clocked in successfully');
    } catch (error: any) {
      console.error('Clock in error:', error);
      toast.error(error.message || 'Failed to clock in');
    } finally {
      setIsLoading(false);
    }
  };

  const initiateClockOut = () => {
    setShowReasonDialog(true);
  };

  const handleClockOut = async () => {
    if (!activeEntry) return;

    setIsLoading(true);
    setShowReasonDialog(false);
    
    try {
      // Phase 2.2: Re-check toggle before proceeding
      const { data: settings } = await supabase
        .from('shop_profile')
        .select('time_clock_enabled')
        .eq('shop_id', activeShop!.id)
        .maybeSingle();

      if (!settings?.time_clock_enabled) {
        toast.error('The time clock feature has been turned off. Please contact your manager.');
        setIsLoading(false);
        return;
      }

      // Check for active timers only if user is a technician
      if (technicianId) {
        const { data: activeTickets } = await supabase
          .from('tickets')
          .select('id, vehicle:vehicles(year, make, model)')
          .eq('assigned_tech_id', technicianId)
          .eq('status', 'Active')
          .eq('actually_started', true);

        if (activeTickets && activeTickets.length > 0) {
          const vehicleInfo = activeTickets[0].vehicle 
            ? `${(activeTickets[0].vehicle as any).year} ${(activeTickets[0].vehicle as any).make} ${(activeTickets[0].vehicle as any).model}`
            : 'a ticket';
          
          toast.warning(`You still have an active timer running on ${vehicleInfo}`);

          // Notify manager
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await supabase.from('notifications').insert({
              shop_id: activeShop!.id,
              user_id: user.id,
              type: 'warning',
              title: 'Clock Out with Active Timer',
              message: `Technician clocked out while timer was running on ${vehicleInfo}`,
              target_roles: ['MANAGER']
            });
          }
        }
      }

      const clockOutTime = clock.now();
      const totalMinutes = Math.round((clockOutTime.getTime() - new Date(activeEntry.clock_in_time).getTime()) / 60000);

      // Determine final reason
      const finalReason = selectedReason === 'Other' ? (customReason || null) : selectedReason;

      const { error } = await supabase
        .from('time_clock_entries')
        .update({
          clock_out_time: clockOutTime.toISOString(),
          total_minutes: totalMinutes,
          clock_out_reason: finalReason
        })
        .eq('id', activeEntry.id);

      if (error) throw error;

      // Optimistic UI update: instantly clear activeEntry for responsive feedback
      setActiveEntry(null);

      toast.success('Clocked out successfully');
      setCustomReason('');
      setSelectedReason('Lunch');
    } catch (error: any) {
      console.error('Clock out error:', error);
      toast.error(error.message || 'Failed to clock out');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if time clock is disabled or no user
  if (!timeClockEnabled || !userId) return null;

  return (
    <>
      <Button
        variant={activeEntry ? "secondary" : "default"}
        size="sm"
        onClick={activeEntry ? initiateClockOut : handleClockIn}
        disabled={isLoading}
        className="gap-2"
        data-testid={activeEntry ? "clock-out-button" : "clock-in-button"}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Clock className="h-4 w-4" />
        )}
        {activeEntry ? (
          <>Clock Out {elapsedTime && `(${elapsedTime})`}</>
        ) : (
          'Clock In'
        )}
      </Button>

      <AlertDialog open={showReasonDialog} onOpenChange={setShowReasonDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clock Out Reason</AlertDialogTitle>
            <AlertDialogDescription>
              Please select a reason for clocking out (optional)
            </AlertDialogDescription>
          </AlertDialogHeader>

          <RadioGroup value={selectedReason} onValueChange={setSelectedReason} className="space-y-2">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Lunch" id="lunch" />
              <Label htmlFor="lunch" className="cursor-pointer">Lunch</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Break" id="break" />
              <Label htmlFor="break" className="cursor-pointer">Break</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Parts Run" id="parts" />
              <Label htmlFor="parts" className="cursor-pointer">Parts Run</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Meeting" id="meeting" />
              <Label htmlFor="meeting" className="cursor-pointer">Meeting</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Training" id="training" />
              <Label htmlFor="training" className="cursor-pointer">Training</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="End of Shift" id="endshift" />
              <Label htmlFor="endshift" className="cursor-pointer">End of Shift</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Other" id="other" />
              <Label htmlFor="other" className="cursor-pointer">Other</Label>
            </div>
          </RadioGroup>

          {selectedReason === 'Other' && (
            <Input
              placeholder="Enter custom reason..."
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              className="mt-2"
            />
          )}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowReasonDialog(false);
              setCustomReason('');
              setSelectedReason('Lunch');
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleClockOut}>
              Clock Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
